import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { contributionsApi, TaxContribution, QuarterlyTotal } from '../services/contributions';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Contributions: React.FC = () => {
  const [contributions, setContributions] = useState<TaxContribution[]>([]);
  const [quarterlyTotals, setQuarterlyTotals] = useState<QuarterlyTotal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | ''>(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState<number | ''>('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedQuarter, tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (tabValue === 0) {
        // Load quarterly totals
        const totals = await contributionsApi.getQuarterlyTotals(
          selectedYear ? Number(selectedYear) : undefined
        );
        setQuarterlyTotals(totals);
      } else {
        // Load individual contributions
        const contribs = await contributionsApi.getAll({
          year: selectedYear ? Number(selectedYear) : undefined,
          quarter: selectedQuarter ? Number(selectedQuarter) : undefined,
        });
        setContributions(contribs);
      }
    } catch (err: any) {
      console.error('Error loading contributions:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load contributions';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getQuarterLabel = (quarter: number) => {
    const labels = ['Q1', 'Q2', 'Q3', 'Q4'];
    return labels[quarter - 1] || `Q${quarter}`;
  };

  const getQuarterMonths = (quarter: number) => {
    const months = [
      'Jan-Mar',
      'Apr-Jun',
      'Jul-Sep',
      'Oct-Dec',
    ];
    return months[quarter - 1] || '';
  };

  if (loading && contributions.length === 0 && quarterlyTotals.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Tax & Social Security Contributions</Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value as number | '')}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {tabValue === 1 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Quarter</InputLabel>
              <Select
                value={selectedQuarter}
                label="Quarter"
                onChange={(e) => setSelectedQuarter(e.target.value as number | '')}
              >
                <MenuItem value="">All</MenuItem>
                {[1, 2, 3, 4].map((q) => (
                  <MenuItem key={q} value={q}>
                    {getQuarterLabel(q)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Quarterly Totals" />
          <Tab label="Individual Contributions" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {quarterlyTotals.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">No quarterly totals found.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Year</TableCell>
                    <TableCell>Quarter</TableCell>
                    <TableCell align="right">Employee Tax</TableCell>
                    <TableCell align="right">Employee SS</TableCell>
                    <TableCell align="right">Company SS</TableCell>
                    <TableCell align="right">Total Contributions</TableCell>
                    <TableCell align="right">Payroll Runs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quarterlyTotals.map((total) => (
                    <TableRow key={`${total.year}-Q${total.quarter}`}>
                      <TableCell>{total.year}</TableCell>
                      <TableCell>
                        <Chip label={getQuarterLabel(total.quarter)} size="small" />
                        <Typography variant="caption" display="block" color="textSecondary">
                          {getQuarterMonths(total.quarter)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(total.totalEmployeeTax, total.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(total.totalEmployeeSocialSecurity, total.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="error">
                          {formatCurrency(total.totalCompanySocialSecurity, total.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          {formatCurrency(total.totalContributions, total.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{total.payrollRunsCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {contributions.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">No contributions found.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Pay Period</TableCell>
                    <TableCell align="right">Employee Tax</TableCell>
                    <TableCell align="right">Employee SS</TableCell>
                    <TableCell align="right">Company SS</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contributions.map((contribution) => {
                    const total =
                      contribution.employeeTaxContribution +
                      contribution.employeeSocialSecurityContribution +
                      contribution.companySocialSecurityContribution;
                    return (
                      <TableRow key={contribution.id}>
                        <TableCell>
                          {format(parseISO(contribution.createdAt), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>{contribution.employeeName || '-'}</TableCell>
                        <TableCell>
                          {contribution.payPeriodStart && contribution.payPeriodEnd ? (
                            <>
                              <Typography variant="body2">
                                {format(parseISO(contribution.payPeriodStart), 'MMM d')} -{' '}
                                {format(parseISO(contribution.payPeriodEnd), 'MMM d, yyyy')}
                              </Typography>
                              <Chip
                                label={`${contribution.year} ${getQuarterLabel(contribution.quarter)}`}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            contribution.employeeTaxContribution,
                            contribution.currency
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            contribution.employeeSocialSecurityContribution,
                            contribution.currency
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="error">
                            {formatCurrency(
                              contribution.companySocialSecurityContribution,
                              contribution.currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(total, contribution.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {contribution.notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Contributions;
